/**
 * useDeckCardActions Hook
 * Verwaltet Aktionen auf Deck-Karten: Quantity ändern, Karte hinzufügen, entfernen.
 * 
 * @param {Object} options
 * @param {Object} options.deck - Das Deck-Objekt mit cards und id
 * @param {boolean} options.canEdit - Ob der Benutzer das Deck bearbeiten darf
 * @param {boolean} [options.canMove=options.canEdit] - Gate für `moveCardToCategory`/
 *   `moveCardsBatch` — eigenständig von `canEdit`, damit Desktop-Drag auf physischen
 *   Decks verschieben darf, ohne die Karten-Editierrechte zu haben
 * @param {Function} options.onDeckUpdate - Callback zum Aktualisieren des Decks
 * @param {Function} [options.onError] - Zeigt dem Nutzer eine Fehlermeldung (Toast)
 */
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { json } from '../../../api/apiClient';
import { errorMessage } from '../../../api/errorMessage';
import { DECK_SUGGESTIONS_ROOT_KEY } from '../catalog/useDeckSuggestions';
import { announceDeckChanged } from './useDeckSyncChannel';

// ========== KONFIGURATIONSKONSTANTEN ==========
// Debounce-Zeit für Autocomplete-Suche (ms)
export const AUTOCOMPLETE_DEBOUNCE_MS = 200;

/**
 * Merged eine (ggf. server-seitig neu entstandene/geänderte) CategoryDTO in den Deck-Cache —
 * idempotent. Wird gebraucht, wenn eine Server-Antwort (Add-Card, Commander-Entfernen, neue
 * Kategorie anlegen) eine Kategorie mitliefert, die im Client noch nicht (oder nicht mehr
 * aktuell) in editorSettings.categories steht — sonst bleiben Konsumenten wie die
 * Stats-Berechnung (excludedCategoryNames in DeckEditorPage/useDeckViewState) bis zum Reload
 * auf dem alten Stand, weil sie ausschließlich aus dem Deck-Cache lesen.
 *
 * Fehlt die ID komplett → anhängen. Ist die ID schon bekannt, aber `excluded`/`name` weichen ab
 * (z.B. weil die Kategorie zwischenzeitlich excluded gesetzt wurde) → Eintrag ersetzen.
 * Ist der Eintrag unverändert → `current` unverändert zurückgeben (Referenz-Stabilität, damit
 * Aufrufer keinen Render-Loop bauen, wenn sie das Ergebnis in einen Effekt zurückspeisen).
 */
export function mergeCategoryIntoDeckCache(current, categoryDTO) {
  if (!categoryDTO || !current) return current;
  const existing = current.editorSettings?.categories;
  const list = Array.isArray(existing) ? existing : [];
  const idx = list.findIndex((c) => c.id === categoryDTO.id);

  if (idx === -1) {
    return {
      ...current,
      editorSettings: {
        ...(current.editorSettings || {}),
        categories: [...list, categoryDTO],
      },
    };
  }

  const existingEntry = list[idx];
  const unchanged = existingEntry.excluded === !!categoryDTO.excluded
    && existingEntry.name === categoryDTO.name;
  if (unchanged) return current;

  const nextList = [...list];
  nextList[idx] = { ...existingEntry, ...categoryDTO };
  return {
    ...current,
    editorSettings: {
      ...(current.editorSettings || {}),
      categories: nextList,
    },
  };
}

/**
 * Hook für Deck-Karten-Aktionen
 */
export function useDeckCardActions({ deck, canEdit = false, canMove = canEdit, onDeckUpdate, onError }) {
  const queryClient = useQueryClient();

  const updateDeck = useCallback((updater) => {
    if (!onDeckUpdate) return;
    onDeckUpdate((prev) => {
      const base = prev || deck;
      if (!base) return prev;
      return updater(base);
    });
  }, [onDeckUpdate, deck]);

  // Nach jeder ändernden Aktion: Wantlist-Cache und Vorschläge invalidieren. Das
  // Wantlist-Panel (im Editor) und die Wantlist-Seite holen frische Daten; die
  // Vorschläge rechnen neu, weil jede Deck-Karte das Profil und den Kandidatenkreis
  // verschiebt und übernommene Karten aus der Liste fallen. Die Meldung an die anderen
  // Fenster desselben Decks (`announceDeckChanged`) gehört an dieselbe Stelle: alle
  // Aufrufer sind hier schon hinter der Server-Antwort.
  const invalidateAfterDeckChange = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['wantlist'] });
    queryClient.invalidateQueries({ queryKey: [DECK_SUGGESTIONS_ROOT_KEY] });
    announceDeckChanged(deck?.id);
  }, [queryClient, deck?.id]);
  
  /**
   * Ändert die Quantity einer Karte im Deck (delta = +1 oder -1)
   */
  const handleQuantityDelta = useCallback(async (scryfallId, delta) => {
    if (!canEdit || !deck || !onDeckUpdate) return { ok: false };
    if (!scryfallId || typeof delta !== 'number' || delta === 0) return { ok: false };

    // Ursprüngliche Position merken: das optimistische Update filtert die Karte bei
    // Minus-auf-0 aus der Liste, ein Revert nach fehlgeschlagenem PATCH muss sie an dieser
    // Stelle wieder einfügen können — ans Ende gehängt würde die Gruppierung durcheinanderbringen.
    const cardSnapshotIndex = (deck.cards || []).findIndex(
      (c) => (c.scryfallId || c.id || c['Scryfall ID']) === scryfallId,
    );
    // Aus dem Schnappschuss statt aus dem Optimistic-Update-Closure gelesen, damit der
    // Rückgabewert auch dann stimmt, wenn onDeckUpdate die Updater-Funktion nicht ausführt.
    const cardSnapshot = cardSnapshotIndex >= 0 ? (deck.cards || [])[cardSnapshotIndex] : undefined;
    const previousQuantity = cardSnapshot ? Number(cardSnapshot.quantity || 1) : undefined;

    updateDeck((current) => {
      const nextCards = (current.cards || [])
        .map((c) => {
          const id = c.scryfallId || c.id || c['Scryfall ID'];
          if (id !== scryfallId) return c;
          const qty = Number(c.quantity || 1);
          return { ...c, quantity: qty + delta };
        })
        .filter((c) => Number(c.quantity || 0) > 0);
      return { ...current, cards: nextCards };
    });

    try {
      const updated = await json(`/api/decks/${deck.id}/cards/${encodeURIComponent(scryfallId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ delta }),
      });

      const newQuantity = typeof updated?.quantity === 'number' ? updated.quantity : null;
      if (newQuantity === null) return { ok: true, removed: false, previousQuantity, newQuantity: previousQuantity, card: cardSnapshot };

      if (newQuantity <= 0) {
        updateDeck((current) => ({
          ...current,
          cards: (current.cards || []).filter(
            (c) => (c.scryfallId || c.id || c['Scryfall ID']) !== scryfallId,
          ),
        }));
      } else {
        updateDeck((current) => ({
          ...current,
          cards: (current.cards || []).map((c) => {
            const id = c.scryfallId || c.id || c['Scryfall ID'];
            return id === scryfallId ? { ...c, quantity: newQuantity } : c;
          }),
        }));
      }

      invalidateAfterDeckChange();
      console.log(`[DeckCardActions] Quantity von ${scryfallId} geändert: delta=${delta}, neu=${newQuantity}`);
      return { ok: true, removed: newQuantity <= 0, previousQuantity, newQuantity, card: cardSnapshot };
    } catch (err) {
      console.error('[DeckCardActions] Fehler beim Aktualisieren der Kartenmenge:', err);
      // Revert (nur betroffene Karte)
      if (previousQuantity !== undefined && previousQuantity !== null) {
        updateDeck((current) => {
          const cards = current.cards || [];
          const idx = cards.findIndex((c) => (c.scryfallId || c.id || c['Scryfall ID']) === scryfallId);
          if (idx === -1) {
            // Das optimistische Update hat die Karte bereits entfernt (Minus-auf-0) —
            // an der Schnappschuss-Position wieder einfügen statt anzuhängen.
            const restoredCard = { ...cardSnapshot, quantity: previousQuantity };
            const insertAt = Math.min(cardSnapshotIndex, cards.length);
            const nextCards = [...cards];
            nextCards.splice(insertAt, 0, restoredCard);
            return { ...current, cards: nextCards };
          }
          return {
            ...current,
            cards: cards.map((c) => {
              const id = c.scryfallId || c.id || c['Scryfall ID'];
              return id === scryfallId ? { ...c, quantity: previousQuantity } : c;
            }),
          };
        });
      }
      onError?.('Kartenmenge konnte nicht geändert werden.');
      return { ok: false };
    }
  }, [canEdit, deck, onDeckUpdate, updateDeck, invalidateAfterDeckChange, onError]);

  /**
   * Fügt eine Karte zum Deck hinzu (per Kartenname)
   */
  const addCardByName = useCallback(async (cardName, { categoryId } = {}) => {
    if (!canEdit || !deck || !onDeckUpdate || !cardName) return null;

    try {
      const result = await json(`/api/decks/${deck.id}/cards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cardName, categoryId }),
      });

      if (result?.card) {
        updateDeck((current) => {
          const withCard = {
            ...current,
            cards: [...(current.cards || []), result.card],
          };
          // Neu entstandene Kategorie (die Rückfall-Logik kann eine neue Kategorie anlegen) sofort im Cache
          // verfügbar machen, sonst rendert sie useDeckViewState erst nach einem Reload.
          return mergeCategoryIntoDeckCache(withCard, result.category);
        });
        invalidateAfterDeckChange();
        console.log(`[DeckCardActions] Karte hinzugefügt: ${cardName}`);
        return { card: result.card, duplicate: false };
      }

      return { card: null, duplicate: true };
    } catch (err) {
      console.error('[DeckCardActions] Fehler beim Hinzufügen der Karte:', err);
      onError?.(errorMessage(err, 'Karte konnte nicht hinzugefügt werden.'));
      return null;
    }
  }, [canEdit, deck, onDeckUpdate, updateDeck, invalidateAfterDeckChange, onError]);

  /**
   * Fügt eine Karte zum Deck hinzu (per Scryfall-ID)
   */
  const addCardByScryfallId = useCallback(async (scryfallId, { categoryId } = {}) => {
    if (!canEdit || !deck || !onDeckUpdate || !scryfallId) return null;

    try {
      const result = await json(`/api/decks/${deck.id}/cards/add-by-scryfall-id`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scryfallId, categoryId }),
      });

      if (result?.card) {
        updateDeck((current) => {
          const exists = (current.cards || []).some((c) => (c.scryfallId || c.id) === (result.card.scryfallId || result.card.id));
          const withCard = exists ? current : {
            ...current,
            cards: [...(current.cards || []), result.card],
          };
          // Siehe addCardByName: neu entstandene Kategorie sofort im Cache verfügbar machen.
          return mergeCategoryIntoDeckCache(withCard, result.category);
        });
        invalidateAfterDeckChange();
        return { card: result.card, duplicate: false };
      }

      return { card: null, duplicate: true };
    } catch (err) {
      console.error('[DeckCardActions] Fehler beim Hinzufügen der Karte per Scryfall-ID:', err);
      onError?.(errorMessage(err, 'Karte konnte nicht hinzugefügt werden.'));
      return null;
    }
  }, [canEdit, deck, onDeckUpdate, updateDeck, invalidateAfterDeckChange, onError]);
  
  /**
   * Entfernt eine Karte komplett aus dem Deck
   */
  const removeCard = useCallback(async (scryfallId) => {
    if (!canEdit || !deck || !onDeckUpdate || !scryfallId) return false;

    const cardToRemove = (deck.cards || []).find(
      (c) => (c.scryfallId || c.id) === scryfallId,
    );
    
    if (!cardToRemove) return false;
    
    // Optimistisch entfernen
    updateDeck((current) => ({
      ...current,
      cards: (current.cards || []).filter((c) => (c.scryfallId || c.id) !== scryfallId),
    }));
    
    try {
      // Quantity auf 0 setzen → Backend entfernt Karte
      await json(`/api/decks/${deck.id}/cards/${encodeURIComponent(scryfallId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ delta: -(cardToRemove.quantity || 1) }),
      });

      invalidateAfterDeckChange();
      console.log(`[DeckCardActions] Karte entfernt: ${scryfallId}`);
      return true;
    } catch (err) {
      console.error('[DeckCardActions] Fehler beim Entfernen der Karte:', err);
      // Revert (best-effort: Karte wieder hinzufügen)
      updateDeck((current) => ({
        ...current,
        cards: [...(current.cards || []), cardToRemove],
      }));
      onError?.('Karte konnte nicht entfernt werden.');
      return false;
    }
  }, [canEdit, deck, onDeckUpdate, updateDeck, invalidateAfterDeckChange, onError]);

  /**
   * Verschiebt eine Karte in eine andere Kategorie (categoryId null = Typ-Fallback des
   * Servers). Nicht optimistisch: der Kategorie-Name ist erst mit der Antwort bekannt,
   * und der einzige Aufrufer (Undo-Gegenaktion) ist nicht latenzkritisch.
   */
  const moveCardToCategory = useCallback(async (scryfallId, categoryId) => {
    if (!canMove || !deck?.id || !onDeckUpdate || !scryfallId) return { ok: false };
    try {
      const response = await json(`/api/decks/${deck.id}/cards/${encodeURIComponent(scryfallId)}/category`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ categoryId: categoryId ?? null }),
      });
      const category = response?.category ?? null;
      if (category) {
        updateDeck((current) => {
          const withCard = {
            ...current,
            cards: (current.cards || []).map((c) => {
              const id = c.scryfallId || c.id || c['Scryfall ID'];
              return id === scryfallId
                ? { ...c, categoryId: category.id ?? null, category: category.name ?? null, isCommander: !!category.isCommander }
                : c;
            }),
          };
          return mergeCategoryIntoDeckCache(withCard, category);
        });
      }
      invalidateAfterDeckChange();
      return { ok: true, category };
    } catch (err) {
      console.error('[DeckCardActions] Fehler beim Verschieben der Karte:', err);
      onError?.(errorMessage(err, 'Karte konnte nicht verschoben werden.'));
      return { ok: false };
    }
  }, [canMove, deck?.id, onDeckUpdate, updateDeck, invalidateAfterDeckChange, onError]);

  /**
   * Verschiebt mehrere Karten in einem Request (`PUT …/cards/categories`, max. 500
   * Einträge). Nicht optimistisch, wie `moveCardToCategory`. Teil-Fehlschläge sind kein
   * HTTP-Fehler — der Server meldet sie je Karte in `results`; nur ein kompletter
   * Request-Fehlschlag geht über `onError`.
   */
  const moveCardsBatch = useCallback(async (moves) => {
    if (!canMove || !deck?.id || !onDeckUpdate || !moves?.length) return null;
    try {
      const response = await json(`/api/decks/${deck.id}/cards/categories`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ moves }),
      });
      const results = response?.results || [];
      const succeeded = results
        .filter((r) => r.ok)
        .map((r) => ({ scryfallId: r.scryfallId, category: r.category }));
      const failed = results
        .filter((r) => !r.ok)
        .map((r) => ({ scryfallId: r.scryfallId, error: r.error }));

      if (succeeded.length > 0) {
        updateDeck((current) => {
          let next = current;
          for (const { category } of succeeded) {
            next = mergeCategoryIntoDeckCache(next, category);
          }
          const succeededIds = new Set(succeeded.map((s) => s.scryfallId));
          const categoryById = new Map(succeeded.map((s) => [s.scryfallId, s.category]));
          return {
            ...next,
            cards: (next.cards || []).map((c) => {
              const id = c.scryfallId || c.id;
              if (!succeededIds.has(id)) return c;
              const category = categoryById.get(id);
              return { ...c, category: category.name, categoryId: category.id, isCommander: !!category.isCommander };
            }),
          };
        });
      }

      invalidateAfterDeckChange();
      return { ok: true, results, succeeded, failed };
    } catch (err) {
      console.error('[DeckCardActions] Fehler beim Batch-Verschieben der Karten:', err);
      onError?.('Verschieben fehlgeschlagen.');
      return null;
    }
  }, [canMove, deck?.id, onDeckUpdate, updateDeck, invalidateAfterDeckChange, onError]);

  /**
   * Setzt die exakte Quantity einer Karte
   */
  const setCardQuantity = useCallback(async (scryfallId, newQuantity) => {
    if (!canEdit || !deck || !onDeckUpdate || !scryfallId) return false;
    
    const card = deck.cards.find(
      (c) => (c.scryfallId || c.id) === scryfallId
    );
    
    if (!card) return false;
    
    const currentQty = card.quantity || 1;
    const delta = newQuantity - currentQty;
    
    if (delta === 0) return true;
    
    await handleQuantityDelta(scryfallId, delta);
    return true;
  }, [canEdit, deck, onDeckUpdate, handleQuantityDelta]);
  
  /**
   * Holt Autocomplete-Vorschläge für Kartennamen
   */
  const fetchAutocomplete = useCallback(async (query) => {
    if (!query || query.trim().length === 0) return [];
    
    try {
      const data = await json(`/api/scryfall/autocomplete?q=${encodeURIComponent(query)}`);
      return data.suggestions || [];
    } catch (err) {
      console.error('[DeckCardActions] Fehler beim Abrufen der Autovervollständigung:', err);
      return [];
    }
  }, []);

  /**
   * Setzt oder entfernt eine Karte als Commander
   * @param {string} cardId - Die ID der Karte (scryfallId)
   * @param {boolean} makeCommander - true = als Commander setzen, false = Commander entfernen
   * @param {Function} onCategoryChange - Callback für optimistisches UI-Update
   */
  const setAsCommander = useCallback(async (cardId, makeCommander, onCategoryChange) => {
    if (!canEdit || !deck?.id) {
      return false;
    }

    // Optimistisches Update
    if (onCategoryChange) {
      onCategoryChange(cardId, makeCommander ? { id: null, name: 'commander', isCommander: true } : null);
    }

    try {
      const response = await json(`/api/decks/${deck.id}/cards/${cardId}/category`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(makeCommander ? { commander: true } : { categoryId: null }),
      });

      if (!response) throw new Error('Fehler beim Ändern des Commander-Status');

      if (onCategoryChange && response?.category) {
        onCategoryChange(cardId, response.category);
      }
      // Beim Commander-Entfernen kann der Server eine neue Typ-Kategorie anlegen
      // (z.B. wenn die Karte als erste ihres Typs zurückfällt) — sofort in den Cache mergen.
      if (response?.category) {
        updateDeck((current) => mergeCategoryIntoDeckCache(current, response.category));
      }

      // Zusätzlich Commander in DeckMetadata setzen (für Kompatibilität)
      if (makeCommander) {
        try {
          await json(`/api/decks/${deck.id}/commander`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ scryfallId: cardId }),
          });
        } catch (metaError) {
          // Nicht kritisch - Kategorie wurde bereits gesetzt
          console.warn('[DeckCardActions] Commander-Metadata konnte nicht gesetzt werden:', metaError);
        }
      }

      invalidateAfterDeckChange();
      return true;
    } catch (error) {
      console.error('[DeckCardActions] Fehler beim Ändern des Commander-Status:', error);
      // Revert bei Fehler
      if (onCategoryChange) {
        onCategoryChange(cardId, makeCommander ? null : { id: null, name: 'commander', isCommander: true });
      }
      // Ohne Meldung sähe der Nutzer nur, wie die Karte kommentarlos zurückspringt —
      // etwa wenn die Commander-Kategorie voll ist (Server antwortet 400 mit Klartext).
      onError?.(errorMessage(error, 'Commander-Status konnte nicht geändert werden.'));
      return false;
    }
  }, [canEdit, deck?.id, updateDeck, invalidateAfterDeckChange, onError]);

  const replaceCardPrint = useCallback(async (oldScryfallId, newScryfallId) => {
    if (!canEdit || !deck?.id || !oldScryfallId || !newScryfallId) {
      return false;
    }
    if (oldScryfallId === newScryfallId) {
      return true;
    }

    try {
      // deck.id ist die UI-ID ("virtual_25" oder "physical_99") — Backend
      // erkennt daran ob deckCard- oder binderCard-Logik greifen muss.
      await json(`/api/decks/${encodeURIComponent(deck.id)}/cards/${encodeURIComponent(oldScryfallId)}/replace-print`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newScryfallId }),
      });

      // Invalidate queries so deck data and wantlist refetch with the
      // authoritative state from the backend (atomic replace endpoint).
      // Prefix-matches all deck-editor / deck-header / deck queries.
      queryClient.invalidateQueries({ queryKey: ['deck-editor'] });
      queryClient.invalidateQueries({ queryKey: ['deck'] });
      queryClient.invalidateQueries({ queryKey: ['wantlist'] });
      queryClient.invalidateQueries({ queryKey: [DECK_SUGGESTIONS_ROOT_KEY] });
      announceDeckChanged(deck.id);

      return true;
    } catch (error) {
      console.error('[DeckCardActions] Fehler beim Ersetzen des Prints:', error);
      onError?.(errorMessage(error, 'Print konnte nicht geändert werden.'));
      return false;
    }
  }, [canEdit, deck?.id, queryClient, onError]);
  
  return {
    handleQuantityDelta,
    addCardByName,
    addCardByScryfallId,
    removeCard,
    moveCardToCategory,
    moveCardsBatch,
    setCardQuantity,
    fetchAutocomplete,
    setAsCommander,
    replaceCardPrint,
  };
}

export default useDeckCardActions;
